import { memo, useState } from 'react';
import { Dropdown, ProgressBar } from 'react-bootstrap';
import { Question, Section } from '../../../response/test.response';
import { useApp } from '../../../hook/app.hook';
import { QuestionType } from '../../../enum/question-type.enum';

// Interface for section-question type combination
interface SectionQuestionTypeCombo {
  id: string;
  sectionId: string;
  sectionName: string;
  questionType: QuestionType;
  questionTypeDisplay: string;
  questionList: Question[];
  active: boolean;
}

const SectionPickerComponent = () => {
  const { testRes, updateActiveSection, updateActiveSectionWithQuestionType } =
    useApp();

  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);

  // Helper function to get question type display name
  const getQuestionTypeDisplayName = (type: QuestionType): string => {
    switch (type) {
      case QuestionType.SINGLE_CHOICE:
        return 'SCQ';
      case QuestionType.MULTIPLE_CHOICE:
        return 'MCQ';
      case QuestionType.INT_ANS:
        return 'Numeric Type';
      case QuestionType.RANGE_ANS:
        return 'Range Answer';
      case QuestionType.MATRIX_MATCH:
        return 'Matrix Match';
      case QuestionType.LONG_ANSWER:
        return 'Paragraph';
      default:
        return type;
    }
  };

  // Generate section-question type combinations for JEE/NEET patterns
  const generateSectionQuestionTypeCombos = (): SectionQuestionTypeCombo[] => {
    const combos: SectionQuestionTypeCombo[] = [];

    testRes.sectionList.forEach((section: Section, sectionIndex: number) => {
      // Group questions by type within each section
      const questionsByType = section.questionList.reduce(
        (acc: Record<QuestionType, Question[]>, question: Question) => {
          if (!acc[question.type]) {
            acc[question.type] = [];
          }
          acc[question.type].push(question);
          return acc;
        },
        {} as Record<QuestionType, Question[]>,
      );

      // Create combo for each question type in the section
      Object.entries(questionsByType).forEach(([questionType, questions]) => {
        const combo: SectionQuestionTypeCombo = {
          id: `${section.id}-${questionType}`,
          sectionId: section.id,
          sectionName: section.name.en,
          questionType: questionType as QuestionType,
          questionTypeDisplay: getQuestionTypeDisplayName(
            questionType as QuestionType,
          ),
          questionList: questions,
          active: section.active && questions.some((q) => q.active),
        };
        combos.push(combo);
      });
    });

    return combos;
  };

  // Check if test pattern is JEE or NEET
  const isJeeNeetPattern = (): boolean => {
    return (
      testRes.pattern == 'jee' ||
      testRes.pattern == 'neet' ||
      testRes.pattern == 'jee-advance' ||
      testRes.pattern == 'nta'
    );
  };

  const selectSectionDropDown = (section: Section) => {
    updateActiveSection(section.id);
    handleDropDownToggle(false);
  };

  const selectSectionQuestionTypeCombo = (combo: SectionQuestionTypeCombo) => {
    updateActiveSectionWithQuestionType(combo.sectionId, combo.questionType);
    handleDropDownToggle(false);
  };

  const handleDropDownToggle = (isOpen: boolean) => {
    setIsSectionDropdownOpen(isOpen);
  };

  // For standard pattern, use original logic
  if (!isJeeNeetPattern()) {
    return (
      <>
        <Dropdown
          show={isSectionDropdownOpen}
          className="w-75"
          onToggle={handleDropDownToggle}
        >
          <Dropdown.Toggle
            variant="light"
            id="section-dropdown"
            className={`${
              isSectionDropdownOpen
                ? 'border-primary section-drop--btn--open bg-light'
                : 'border-blue'
            } font-weight-500 rounded ft-14 w-100 text-start section-drop--btn d-flex align-items-center`}
          >
            {
              testRes.sectionList.filter(
                (section: Section) => section.active,
              )[0].name.en
            }
          </Dropdown.Toggle>

          <Dropdown.Menu className="w-100 border-0 shadow">
            <ul className="list-style-none p-0 m-0 dropdown-section-menu overflow-auto">
              {testRes.sectionList.map((section: Section, index: number) => {
                let totalCount = section.questionList.length;
                let attemptedCount = section.questionList.filter(
                  (question: Question) => question.attempted,
                ).length;

                const progress = (attemptedCount * 100) / totalCount;
                return (
                  <li
                    key={section.id}
                    onClick={() => {
                      selectSectionDropDown(section);
                    }}
                    className="px-2 pb-3 border-bottom section--ques-list"
                  >
                    <div className="d-flex align-items-center ft-14">
                      <span className="section-list-badge bg-primary text-white p-2 d-flex align-items-center justify-content-center rounded">
                        {index + 1}
                      </span>

                      <div className="ms-3 w-100 d-flex">
                        <div className="d-flex flex-column w-100">
                          <div className="font-weight-600">
                            {section.name.en}
                          </div>
                          <div className="text-nowrap d-flex align-items-center small mt-1 text-dimgray">
                            {progress.toFixed(0)}% Done
                            <ProgressBar
                              variant="primary"
                              now={progress}
                              className="custom-progress--bar w-100 ms-2"
                            />
                          </div>
                        </div>
                        <div className="ml-2"></div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Dropdown.Menu>
        </Dropdown>
      </>
    );
  }

  // For JEE/NEET pattern, show section-question type combinations
  const sectionQuestionTypeCombos = generateSectionQuestionTypeCombos();
  const activeCombo = sectionQuestionTypeCombos.find((combo) => combo.active);

  return (
    <>
      <Dropdown
        show={isSectionDropdownOpen}
        className="w-75"
        onToggle={handleDropDownToggle}
      >
        <Dropdown.Toggle
          variant="light"
          id="section-dropdown"
          className={`${
            isSectionDropdownOpen
              ? 'border-primary section-drop--btn--open bg-light'
              : 'border-blue'
          } font-weight-500 rounded ft-14 w-100 text-start section-drop--btn d-flex align-items-center`}
        >
          {activeCombo
            ? `${activeCombo.sectionName} - ${activeCombo.questionTypeDisplay}`
            : 'Select Section'}
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100 border-0 shadow">
          <ul className="list-style-none p-0 m-0 dropdown-section-menu overflow-auto">
            {sectionQuestionTypeCombos.map(
              (combo: SectionQuestionTypeCombo, index: number) => {
                let totalCount = combo.questionList.length;
                let attemptedCount = combo.questionList.filter(
                  (question: Question) => question.attempted,
                ).length;

                const progress = (attemptedCount * 100) / totalCount;
                return (
                  <li
                    key={combo.id}
                    onClick={() => {
                      selectSectionQuestionTypeCombo(combo);
                    }}
                    className="px-2 pb-3 border-bottom section--ques-list"
                  >
                    <div className="d-flex align-items-center ft-14">
                      <span className="section-list-badge bg-primary text-white p-2 d-flex align-items-center justify-content-center rounded">
                        {index + 1}
                      </span>

                      <div className="ms-3 w-100 d-flex">
                        <div className="d-flex flex-column w-100">
                          <div className="font-weight-600">
                            {combo.sectionName} - {combo.questionTypeDisplay}
                          </div>
                          <div className="text-nowrap d-flex align-items-center small mt-1 text-dimgray">
                            {progress.toFixed(0)}% Done
                            <ProgressBar
                              variant="primary"
                              now={progress}
                              className="custom-progress--bar w-100 ms-2"
                            />
                          </div>
                        </div>
                        <div className="ml-2"></div>
                      </div>
                    </div>
                  </li>
                );
              },
            )}
          </ul>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default memo(SectionPickerComponent);
